import { Component } from 'react'
import { observer } from 'mobx-react'
import { observable, makeObservable, configure } from 'mobx'
import { Route, RouteComponentProps, withRouter } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.min.css'
import { t } from 'ttag'
import { isMobileOnly } from 'react-device-detect'
import _debug from 'debug'

import StatusEditor from '../status/StatusEditor'
import DatabaseEditor from './DatabaseEditor'
import ProjectsEditor from './ProjectsEditor'
import PortionsEditor from '../portions/PortionsEditor'
import NavigationBar from './NavigationBar'
import './SLTool.css'
import { AppRoot } from '../../models3/AppRoot'
import { displayError, logError, systemError } from '../utils/Errors'
import { RootProvider } from './RootContext'
import TitleComponent from './Title'
import ProjectSettings from '../projectSettings/ProjectSettings'
import { ErrorBoundary } from '../utils/Errors'
import LoadingMessage from '../utils/InitializationMessage'
import { Passage, Project } from '../../models3/ProjectModels'
import TranslationEditor from '../translation/TranslationEditor'
import NotAMember from './NotAMember'
import ProjectMessages from '../notifications/ProjectMessages'
import { LogoutOnIdTokenExpiration } from './Login'
import { Auth0Login } from './Auth0'
import { LDProvider } from 'launchdarkly-react-client-sdk'
import { fmt } from '../utils/Fmt'
import { LDContextChangeListener } from './LDContextChangeListener'
import { TRLProjectsLoader } from '../TRL/TRLProjectsLoader'
import { LocalStorageSetter } from './LocalStorageSetter'
import { ShowAnnouncements } from '../notifications/ProjectAnnouncements'
import SlttAppAuth0Login from './SlttAppAuth0Login'
import { isSlttAppClient } from '../utils/Auth0Utils'

configure({
    enforceActions: 'never', // don't require all updates to observable to occur in actions
})

    // SLTool - top level component for app

const log = _debug('sltt:SLTool')

    let iPadWarningIssued = false

    const _window = window as any

    function isChrome() {
        let _w: any = window

        let isChromium = _w.chrome
        let winNav = _w.navigator
        let vendorName = winNav.vendor
        let isOpera = typeof _w.opr !== "undefined"
        let isIEedge = winNav.userAgent.indexOf("Edge") > -1
        let isIOSChrome = winNav.userAgent.match("CriOS")

        if (isIOSChrome) { return true } 
        else if (
            isChromium !== null &&
            typeof isChromium !== "undefined" &&
            vendorName === "Google Inc." &&
            isOpera === false &&
            isIEedge === false
        ) { return true }

        return false
    }

    function issueIPadWarning() {
        if (iPadWarningIssued) return false
        iPadWarningIssued = true
        return navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
    }

    const DEFAULT_PAGE = '/' // translation (video tab) page

    class SLTool extends Component<RouteComponentProps> {
        @observable selected = DEFAULT_PAGE
        @observable uiLanguageChanging = false
        narrowScreenListener?: MediaQueryList
        appRoot: AppRoot
        
        constructor(props: any) {
            super(props)
            makeObservable(this)

            this.appRoot = new AppRoot(this.selectPage.bind(this))
            _window.appRoot = this.appRoot
            _window._ = this.appRoot // less to type
            // VideoCacheUploader.uploadDone = this.appRoot.uploadDone.bind(this.appRoot)

            this.appRoot.useMobileLayout = isMobileOnly
            let { origin, pathname, hash, search } = window.location
            // Older versions of SLTT have the id_token embedded in the hash part of the url
            if (!search) {
                let nonHashURL = origin.concat(pathname).concat(hash.slice(2))
                search = (new URL(nonHashURL)).search ?? ''
            }
            this.appRoot.checkForRegistration(search)

            const { location } = props
            this.selectPage(location.pathname || localStorage.getItem('pageSelected') || DEFAULT_PAGE)

            this.selectProject = this.selectProject.bind(this)
        }

        async componentDidMount() {
            let { appRoot } = this
            let { useMobileLayout } = appRoot
            log('componentDidMount')

            this.narrowScreenListener = window.matchMedia('(max-width: 800px)')
            this.setNarrowWidth(this.narrowScreenListener.matches)
            this.narrowScreenListener.addEventListener('change', (e) => {
                this.setNarrowWidth(e.matches)
            })
            
            let videoCacheLimitGB = 10
            if (useMobileLayout) {
                videoCacheLimitGB = 2
            }

            localStorage.setItem('videoCacheLimitGB', videoCacheLimitGB.toString())

            const rt = await appRoot.initializeProjects()
            
            // If the user has not created any portions for this project yet
            // force them to the portion creation page.
            const portionsCount = rt?.project.portions.length ?? -1
            if (portionsCount === 0) {
                log('force /portions')
                this.props.history.push(`/portions`)
                this.selectPage('/portions')
                return
            }
        }

        componentWillUnmount() {
            this.narrowScreenListener?.removeEventListener('change', (e) => {
                this.setNarrowWidth(e.matches)
            })
        }

        setNarrowWidth(value: boolean) {
            let { appRoot } = this
            appRoot.useNarrowWidthLayout = value
            appRoot.rts.forEach(rt => rt.useNarrowWidthLayout = value)
        }

        render() {
            let { selected, appRoot, selectProject } = this
            let { rts, rt, iAmRoot, projectsInitialized, announcementsRt } = appRoot
            const { initialized: rtInitialized } = rt ?? {}

            if (!isChrome())
                return (<div className="not-chrome">{/* translator: important */ t`You must use Chrome browser for SLTT.`}</div>)

            if (issueIPadWarning())
                displayError(t`SLTT does not work on iPad.`)

            if (appRoot.id_token === '') {
                if (localStorage.getItem('sltt_id_token')) {
                    // race condition?
                    // sometimes right after the user logs in,
                    // they are asked to login again.
                    // may have something to do with service worker updates?
                    // although that should not have such a side-effect
                    logError('No id_token in appRoot, but found sltt_id_token in localStorage.')
                }
                if (isSlttAppClient) {
                    return <SlttAppAuth0Login appRoot={appRoot} />
                }
                return (<Auth0Login {...{appRoot}} />)
            }

            let selectPage = this.selectPage.bind(this)
            let projectName = rt ? rt.displayName : ''
            let portionName = rt && rt.portion ? rt.portion.name : ''
            let title = projectName && portionName ? `${projectName}/${portionName}` : 'Sign Language Translation Tool'

            /* Debugging routes.
             * It may be useful to put the following in react-router/es/matchPath.js>matchPath
             *
             *    console.log('!!!matchPath', pathname, path)
             */

            // I think Router matches against window.location.pathname

            async function selectPassage(passage: Passage) {
                log('selectPassage', fmt({passage}))

                // Find the portion corresponding to this passage and select the portion
                const portion = rt?.project.findPortion(passage._id)
                if (!portion) {
                    log('### could not find matching portion')
                    return
                }
                await rt!.setPortion(portion)
                await rt!.setPassage(passage)
                window.scrollTo(0, 0) // scroll to top when new passage selected
                selectPage('/')
            }

            // console.log('render SLTool', rt)

            return (
                <RootProvider initialValue={rt}>
                    <LDProvider clientSideID={import.meta.env.VITE_LD_CLIENT_KEY!}>
                        <div>
                            <LogoutOnIdTokenExpiration {...{ appRoot, isRecording: rt ?? undefined }} />
                            <LocalStorageSetter />
                            {rt && rtInitialized && projectsInitialized && <LDContextChangeListener rt={rt} />}
                            {rt && projectsInitialized && <TRLProjectsLoader rt={rt} />}
                            <div>
                                <TitleComponent title={title} />
                                <NavigationBar
                                    appRoot={appRoot}
                                    selected={selected}
                                    selectPage={selectPage}
                                    setChangingLanguage={this.setChangingLanguage}
                                    openTour={rt && rt.openTour.bind(rt)}
                                    selectProject={selectProject}
                                />

                                <div className="app-content">
                                    {appRoot.id_token && !projectsInitialized && !this.uiLanguageChanging && (
                                        <LoadingMessage loadingMessage={t`Initializing projects ...`} />
                                    )}

                                    {appRoot.id_token && projectsInitialized && !this.uiLanguageChanging && (
                                        <>
                                            {rts.length === 0 && <NotAMember appRoot={appRoot} />}
                                            {rt && (
                                                <Route exact={true} path={`/(index.html)?`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            {rt && <TranslationEditor {...{ rt }} />}
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            )}

                                            {rt &&
                                                <Route path={`/portions`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            <PortionsEditor rt={rt!} />
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            }

                                            {rt &&
                                                <Route path={`/project-settings`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            <ProjectSettings rt={rt!} appRoot={appRoot} />
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            }

                                            {rt &&
                                                <Route path={`/messages`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            <ProjectMessages rt={rt!} appRoot={appRoot} />
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            }

                                            {rt &&
                                                <Route path={`/status`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            <StatusEditor rt={rt!} selectPassage={selectPassage} />
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            }

                                            {rt && rt.iAmRoot &&
                                                <Route path={`/database`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            <DatabaseEditor rt={rt!} />
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            }
                                            {iAmRoot &&
                                                <Route path={`/projects`}
                                                    render={(props: any) => (
                                                        <ErrorBoundary>
                                                            <ProjectsEditor appRoot={appRoot} />
                                                        </ErrorBoundary>
                                                    )}
                                                />
                                            }
                                        </>
                                    )}
                                </div>
                            </div>
                            {rtInitialized && 
                                <ShowAnnouncements appRoot={appRoot} announcementsRt={announcementsRt}/>}
                            <ToastContainer
                                position={toast.POSITION.TOP_LEFT}
                                autoClose={5000}
                                hideProgressBar={true}
                                newestOnTop={false}
                                closeOnClick
                                pauseOnHover
                                className={'sltt-toast-container'}
                            />
                        </div>
                    </LDProvider>
                </RootProvider>
            )
        }

        selectPage(selection: string) {
            let finalSelection = selection
            if (selection === '/index.html') {
                finalSelection = '/'
            }
            this.selected = finalSelection
            log('selectPage', selection, finalSelection, { pageSelected: localStorage.pageSelected })
            localStorage.setItem('pageSelected', finalSelection)
        }

        selectProject(project: Project) {
            this.appRoot.setCurrentProject(project.name)
                .catch(systemError)
        }

        setChangingLanguage = (status: boolean) => {
            this.uiLanguageChanging = status
            // setTimeout(() => {
            //     this.uiLanguageChanging = false
            // }, 2000)
        }
    }

    export default withRouter<any,any>(observer(SLTool))